import React, { useRef, useEffect, useState } from 'react';
import { useLoader } from '../context/LoaderContext';
const ScrollImageSequence = ({ frameCount = 300, containerRef }) => {
  const canvasRef = useRef(null);
  const [images, setImages] = useState([]);
    const [localLoadedCount, setLocalLoadedCount] = useState(0); // Renamed to avoid confusion
    const { setProgress, setLoading } = useLoader();

    // Preload images with batching to avoid blocking UI
  useEffect(() => {
      const imgArray = [];
      for (let i = 0; i < frameCount; i++) {
          imgArray.push(new Image());
      }
      setImages(imgArray);

    let count = 0;
      let isMounted = true;

      // Reset loader state on mount
      setProgress(0);
      setLoading(true);

      const loadBatch = (startIndex) => {
          if (!isMounted) return;
          const batchSize = 20; // Load 20 images at a time
          const endIndex = Math.min(startIndex + batchSize, frameCount);

          for (let i = startIndex; i < endIndex; i++) {
              const img = imgArray[i];
              const frameNumber = (i + 1).toString().padStart(4, '0');
              const src = `/frames/frame_${frameNumber}.jpg`;

              img.onload = () => {
                  if (!isMounted) return;
                  count++;
                  setLocalLoadedCount(prev => prev + 1);

                  // Update global progress
                  const percentage = (count / frameCount) * 100;
                  setProgress(percentage);

                  if (count === 1) console.log('First frame loaded:', src);

                  // USER REQUEST: Dismiss loader early (after 10 frames)
                  if (count === 10) {
                      setLoading(false);
                  }

                  if (count === frameCount) {
                      console.log('All frames loaded');
                      // setTimeout(() => setLoading(false), 500); // Removed as we dismiss early now
                  }
              };
              img.onerror = (e) => {
                  console.error(`Failed to load frame: ${src}`, e);
                  if (!isMounted) return;
                  count++;
                  const percentage = (count / frameCount) * 100;
                  setProgress(percentage);
                  if (count === frameCount) {
                      setTimeout(() => setLoading(false), 500);
                  }
              };
              img.src = src;
          }

          if (endIndex < frameCount) {
              // Schedule next batch
              setTimeout(() => loadBatch(endIndex), 50);
          }
      };

      // Start loading
      loadBatch(0);

      return () => {
          isMounted = false;
          setLoading(false);
      };
  }, [frameCount, setProgress, setLoading]);

  // Update canvas on scroll
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || images.length === 0) return;

    const context = canvas.getContext('2d');
    const scrollTarget = containerRef?.current || window;

    const render = () => {
        let scrollTop = 0;
        let scrollHeight = 0;
        let clientHeight = 0;

        if (containerRef && containerRef.current) {
            scrollTop = containerRef.current.scrollTop;
            scrollHeight = containerRef.current.scrollHeight;
            clientHeight = containerRef.current.clientHeight;
        } else {
            scrollTop = window.scrollY;
            scrollHeight = document.documentElement.scrollHeight;
            clientHeight = window.innerHeight;
        }

        const totalScrollableDistance = scrollHeight - clientHeight;
        // Map scroll percentage to frame index
        const scrollPercentage = Math.min(Math.max(scrollTop / totalScrollableDistance, 0), 1);
        
        const frameIndex = Math.min(
            Math.floor(scrollPercentage * (images.length - 1)),
            images.length - 1
        );

        const img = images[frameIndex];
        
        if (img && img.complete) {
             // Draw image to cover the canvas
            const hRatio = canvas.width / img.width;
            const vRatio = canvas.height / img.height;
            const ratio = Math.max(hRatio, vRatio);
            
            const centerShift_x = (canvas.width - img.width * ratio) / 2;
            const centerShift_y = (canvas.height - img.height * ratio) / 2;
            
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(
                img,
                0, 0, img.width, img.height,
                centerShift_x, centerShift_y, img.width * ratio, img.height * ratio
            );
        }
    };

    // Initial render
    render();

    // Resize handler
    const handleResize = () => {
        const dpr = window.devicePixelRatio || 1;
        // set the size of the drawingBuffer based on the devicePixelRatio
        canvas.width = window.innerWidth * dpr;
        canvas.height = window.innerHeight * dpr;

        // Ensure the style is set correctly for layout (logical pixels)
        canvas.style.width = `${window.innerWidth}px`;
        canvas.style.height = `${window.innerHeight}px`;

        render();
    };
    
    // Attach listeners
    scrollTarget.addEventListener('scroll', render);
    window.addEventListener('resize', handleResize);
    
      // Initial setup
      handleResize();

    // Call render once images load (in case scroll didn't happen)
    // We can rely on loadedCount causing re-effect, which calls render()
    
    return () => {
        scrollTarget.removeEventListener('scroll', render);
        window.removeEventListener('resize', handleResize);
    };
  }, [images, containerRef, localLoadedCount]); // Re-run when images load or ref changes

  return (
    <canvas
      ref={canvasRef}
      style={{
          position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none', // Allow clicks to pass through
      }}
    />
  );
};

export default ScrollImageSequence;
